import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { StatusCodes } from 'http-status-codes';
import styled from 'styled-components';
import Barcode from 'react-barcode';

import { OrderGetMinDTO, ProductGetDTO, QuantityGetDto, WarehouseLocationGetDTO } from 'svila-common';
import { PalletGetDTO } from 'svila-common/types/back-end/palletGetDTO';

import WhitePad from '../../../../../components/common/WhitePad/WhitePad';
import SecondaryNavigation from '../../../../../components/common/navigation/SecondaryNavigation';
import TitleComponent from '../../../../../components/common/forms/Title/TitleComponent';
import PalletDescription from './components/PalletDescription';
import PalletDescriptionsApi from '~/src/services/api/PalletDescriptionsApi';
import Loader from '~/src/components/common/Loader';
import fnbLogo from '~/src/static/img/svila-logo.jpg';
import backgroundLine from '~/src/static/img/BackgroundLine.png';

import { OrderWithoutProductsDto, PalletDescriptionWithOrder } from './PalletDescriptions';

export default function PalletDescriptionPrint(): JSX.Element {
  const { id } = useParams();
  const [itemData, setItemData] = useState<PalletDescriptionWithOrder>([]);
  const [order, setOrder] = useState<OrderWithoutProductsDto | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingError, setLoadingError] = useState<string | null>(null);

  const getItem = async (): Promise<void> => {
    setLoading(true);
    const result = await PalletDescriptionsApi.getPalleDescription(id, true);
    if (result.status === StatusCodes.OK) {
      setItemData(result.data);
    } else {
      setLoadingError('Error loading data');
    }
    setLoading(false);
  };

  useEffect(() => {
    getItem();
  }, []);

  return (
    <WhitePad isFluid>
      <SecondaryNavigation isConsolidation={false} />
      <table>
        <thead>
          <tr>
            <td>
              <Wrapper style={{ display: 'block' }}>
                <Box style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Content style={{ flexDirection: 'row-reverse', marginTop: 5 }}>
                    <Logo src={fnbLogo} alt="fnbLogo" />
                  </Content>
                </Box>
              </Wrapper>
            </td>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <Wrapper style={{ display: 'block' }}>
                <Box style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Content style={{ marginTop: 5 }}>
                    <span>SVILA TRADING LTD</span>
                    <Text>
                      Phone: <b>+359 896 897 685</b>
                    </Text>
                    <Text>
                      Warehouse Sevlievo: <b>41 Marin Popov st, 5400, sevlievo Bulgaria</b>
                    </Text>
                    <b style={{ color: '#FF3155' }}>www.chocolate-brands.com</b>
                  </Content>
                  <Content style={{ flexDirection: 'row', justifyContent: 'center' }}>
                    <Barcode value={itemData?.order?.orderNumber} height={40} width={3} flat={true} />
                  </Content>
                </Box>
                <Box style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Content>
                    <GreyText>COSIGNEE:</GreyText>
                    <Text>
                      Customer: <b>{itemData?.order?.customer?.name}</b>
                    </Text>
                    <Text>
                      Cosignee: <b>{itemData?.order?.cosigneeName}</b>
                    </Text>
                    <Text>
                      Cosignee Address: <b>{itemData?.order?.cosigneeAddress}</b>
                    </Text>
                    <Text>
                      Destination: <b>{itemData?.order?.destination}</b>
                    </Text>
                  </Content>
                  <Content style={{ justifyContent: 'space-between' }}>
                    <GreyText>TRANSPORT:</GreyText>
                    {itemData?.order?.shippingType === 'Sea' ? (
                      <>
                        <Text style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span>Container Line:</span> <b> {itemData?.order?.containerLine} </b>
                        </Text>
                        <Text style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span>Container #:</span> <b> {itemData?.order?.containerNo} </b>
                        </Text>
                        <Text style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span>Container Type:</span> <b> {itemData?.order?.container.name} </b>
                        </Text>
                        <Text>
                          Vessel Flag: <b>{itemData?.order?.vesselFlag}</b>
                        </Text>
                      </>
                    ) : (
                      <Text>
                        Truck/Trailer Plate: <b>{itemData?.order?.truckTrailer}</b>
                      </Text>
                    )}
                  </Content>
                  <Content style={{ alignItems: 'flex-end' }}>
                    <GreyText>More:</GreyText>

                    <Text>
                      Shipping Type:<b>{itemData?.order?.shippingType}</b>
                    </Text>
                    <Text>
                      Routing:<b>{itemData?.order?.routing}</b>
                    </Text>
                  </Content>
                </Box>
                <div>
                  <BackgroundLine src={backgroundLine} alt="backgroundLine" />
                </div>
                <TitleComponent title="Loaded Pallets Description" />

                {loading ? (
                  <Loader />
                ) : loadingError ? (
                  <div>{loadingError}</div>
                ) : itemData?.palletData?.length === 0 ? (
                  <div>No data</div>
                ) : (
                  <div>
                    {itemData?.palletData?.map((locationWithQuantities, index: number) => {
                      return (
                        <PalletDescription
                          key={index}
                          item={locationWithQuantities}
                          index={index}
                          showForm={false}
                        />
                      );
                    })}
                  </div>
                )}
              </Wrapper>
            </td>
          </tr>
        </tbody>
      </table>
    </WhitePad>
  );
}

const Logo = styled.img`
  width: 160px;
  height: 76px;
`;

const TheadStyle = styled.thead`
  border-bottom: 1px solid #eaeaea;
`;

const Wrapper = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const TableStyle = styled.table`
  width: 100%;
  margin-bottom: 20px;
  padding-bottom: 50px;
  &tr:nth-child(odd) {
    background-color: #eee;
  }
  &tr:nth-child(even) {
    background-color: #fff;
  }
`;

const GreyText = styled.span`
  font-size: 16px;
  font-weight: 500;
  color: #999;
`;

const Text = styled.p`
  font-size: 16px;
  margin: 0;
`;

const TdStyle = styled.td`
  font-size: 16px;
  padding: 10px;
`;

const Content = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 11px 12px;
  flex: 1;
`;

const Box = styled.div`
  display: flex;
  justify-content: space-between;
`;

const TrStyle = styled.tr`
  border-bottom: 1px solid #eaeaea;
`;

const TBodyStyles = styled.tbody`
  // ${TrStyle}:nth-child(even) {
  //   background: #eee;
  // }
`;

const BackgroundLine = styled.img`
  width: 100%;
  margin: 30px 0;
`;
